'use client';

import { useAuth } from '@/context/AuthContext';
import { useUserRole } from '@/context/UserRoleContext';

/**
 * Whether this viewer may use a public self-service flow — enrolling, joining a
 * waitlist, booking a lesson, registering for a tryout.
 *
 * The public pages used to ask `currentUser?.userType === 'staff'` and let
 * everyone else through. That is allow-by-default, and it reads two very
 * different situations as "not staff": we are still checking, and we could not
 * check. Both walked an unidentified viewer onto the enrollment form. Ask the
 * opposite question instead — have we CONFIRMED this viewer may self-serve? —
 * so an unknown answer blocks the money action rather than permitting it.
 *
 * Browsing is deliberately not gated on this. A catalog or class page must
 * render for anyone; only the action is withheld. That distinction is the whole
 * point of the fix this hook ships with.
 */
export type SelfServiceAccess =
  /** Still resolving — show a loading state, decide nothing yet. */
  | 'checking'
  /** Signed in, but we could not establish who they are. Block, offer a retry. */
  | 'unconfirmed'
  /** Confirmed staff. Block with the staff message. */
  | 'staff'
  /** Confirmed able to self-serve — including logged-out visitors. */
  | 'allowed';

export function useSelfServiceAccess(): SelfServiceAccess {
  const { user, isLoading: authLoading } = useAuth();
  const { currentUser, isLoading: roleLoading, roleResolution } = useUserRole();

  // Order matters: this MUST come before the !user check. Reversing it would
  // read a signed-in staff member as 'allowed' during the auth gap.
  // (roleLoading is already true whenever roleResolution === 'loading'.)
  if (authLoading || roleLoading) return 'checking';

  // Logged-out visitors are the primary audience for these pages — they sign up
  // as part of the flow. There is no role to confirm, so nothing to block on.
  if (!user) return 'allowed';

  if (roleResolution === 'unconfirmed') return 'unconfirmed';

  // Allow only on a profile loaded for THIS account (every profile carries its
  // account's id). The role context already reads as loading until it has
  // one; this is the backstop, so no page acts on another account's role, or
  // on no role at all, while a sign-in or a re-check is still settling.
  if (currentUser?.id !== user.id) return 'checking';

  return currentUser?.userType === 'staff' ? 'staff' : 'allowed';
}
